import React, { useEffect, useMemo, useState } from 'react'
import { Form } from 'react-bootstrap'
import {
  Accordion,
  AccordionItem,
  Button,
  Combobox,
  InputField,
  Select,
} from '@enamelai/design-system'
import classNames from 'classnames'
import {
  isPerioSection,
  PerioChartSectionData,
  PerioSection,
} from '@dataTypes/chartingFeature.types'
import {
  getProbeSiteLabels,
  getToothIndex,
  toDisplayOrder,
  toStorageOrder,
} from '@utils/perio.utils'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { useActivePerioChart } from '@hooks/useActivePerioChart'
import { getFullName } from '@utils/fullname.util'
import {
  CHART_PROVIDER_INVALID_ERROR,
  isChartProviderHardError,
} from '@utils/chart-validation.utils'
import {
  buildProviderSearchList,
  resolveChartProviderDisplayName,
} from '@utils/provider-options.util'
import {
  setCreatePerioChart,
  setPerioUpdated,
} from '@store/reducers/charting.reducer'
import ChartNameComboBox from '@components/pages/Charting/ChartNameComboBox.component'

interface PerioSidePanelProps {
  voiceStatus?: React.ReactNode
  /** When true (mic connecting/listening/processing), open Voice transcription. */
  voiceActive?: boolean
  /** Bumped when manual save fails on Charting information fields. */
  chartingInfoFocusTick?: number
  voiceMeasurement?: React.ReactNode
  selectedTooth: number
  setSelectedTooth: (tooth: number) => void
  selectedSection: PerioSection
  setSelectedSection: (section: PerioSection) => void
  validationErrors: { [key: string]: string }
  updatePerioValue: (
    section: PerioSection,
    label: keyof PerioChartSectionData,
    toothIndex: number,
    newValue: string,
  ) => void
  setValidationErrors: React.Dispatch<
    React.SetStateAction<{
      [key: string]: string
    }>
  >
}

const CHARTING_INFO_ACCORDION_KEY = '0'
const VOICE_TRANSCRIPTION_ACCORDION_KEY = '2'
const PERIO_AREA_OPTIONS = [
  { value: PerioSection.Facial, label: 'Facial' },
  { value: PerioSection.Lingual, label: 'Lingual' },
]
const openIdsFromKey = (key: string): string[] => (key ? [key] : [])

type PerioFormRowProps = {
  label?: React.ReactNode
  htmlFor?: string
  tight?: boolean
  className?: string
  controlClassName?: string
  children: React.ReactNode
}

const PerioFormRow = ({
  label,
  htmlFor,
  tight = false,
  className,
  controlClassName,
  children,
}: PerioFormRowProps): React.ReactElement => {
  const labelNode = htmlFor ? (
    <label htmlFor={htmlFor} className="form-row-label">
      {label}
    </label>
  ) : (
    <div className="form-row-label">{label}</div>
  )
  return (
    <div
      className={classNames(
        'perio-form-row',
        tight && 'perio-form-row--tight',
        className,
      )}
    >
      {labelNode}
      <div className={classNames('form-row-control', controlClassName)}>
        {children}
      </div>
    </div>
  )
}

const PerioSidePanel = ({
  voiceStatus,
  voiceActive = false,
  chartingInfoFocusTick = 0,
  voiceMeasurement,
  selectedTooth,
  setSelectedTooth,
  selectedSection,
  setSelectedSection,
  updatePerioValue,
  validationErrors,
  setValidationErrors,
}: PerioSidePanelProps): React.ReactElement => {
  const dispatch = useAppDispatch()
  const { activePerioChart, isExplicitHistorySelection, isPersistedChart } =
    useActivePerioChart()
  const createPerioChart = useAppSelector(
    (state) => state.charting.createPerioChart,
  )
  const selectedPerioChart = useAppSelector(
    (state) => state.charting.selectedPerioChart,
  )
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const providers = useAppSelector((state) => state.provider.data)
  const selectedClinicId = useAppSelector(
    (state) => state.clinic.selectedClinic?.clinic_id,
  )
  const isPerioLocked = useAppSelector((state) => state.charting.isPerioLocked)
  const isViewMode = isExplicitHistorySelection
  const providerSearchList = useMemo(
    () => buildProviderSearchList(providers, selectedClinicId),
    [providers, selectedClinicId],
  )

  const [provId, setProvId] = useState('')
  const [chartName, setChartName] = useState('')
  const [activeAccordionKey, setActiveAccordionKey] = useState<string>(
    CHARTING_INFO_ACCORDION_KEY,
  )
  const providerOptions = useMemo(
    () =>
      providerSearchList.map((provider) => ({
        value: provider.id,
        label: provider.name,
      })),
    [providerSearchList],
  )
  const draftProviderId = createPerioChart
    ? createPerioChart.provider_id
    : (provId ?? '')
  const providerComboboxValue = useMemo(() => {
    const match = providerSearchList.find(
      (provider) => provider.id === draftProviderId,
    )
    return match ? match.name : draftProviderId
  }, [draftProviderId, providerSearchList])
  const resolveProviderId = (nextValue: string): string => {
    const trimmed = nextValue.trim()
    if (!trimmed) {
      return nextValue
    }
    const byId = providerSearchList.find((provider) => provider.id === trimmed)
    if (byId) {
      return byId.id
    }
    const byName = providerSearchList.find(
      (provider) => provider.name.toLowerCase() === trimmed.toLowerCase(),
    )
    return byName?.id ?? nextValue
  }
  const handleProviderChange = (nextValue: string): void => {
    const nextProviderId = resolveProviderId(nextValue)
    const previousProviderId = createPerioChart?.provider_id ?? ''
    if (nextProviderId === previousProviderId) {
      return
    }
    const isValidProvider = providerSearchList.some(
      (provider) => provider.id === nextProviderId,
    )
    setProvId(nextProviderId)
    dispatch(
      setCreatePerioChart({
        ...createPerioChart,
        provider_id: nextProviderId,
      }),
    )
    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors }
      if (nextProviderId.trim() === '' || isValidProvider) {
        delete updatedErrors.provider_id
      } else {
        updatedErrors.provider_id = CHART_PROVIDER_INVALID_ERROR
      }
      return updatedErrors
    })
    dispatch(setPerioUpdated(true))
  }

  useEffect(() => {
    if (!voiceActive) {
      return
    }
    setActiveAccordionKey(VOICE_TRANSCRIPTION_ACCORDION_KEY)
  }, [voiceActive])

  useEffect(() => {
    if (chartingInfoFocusTick <= 0) {
      return
    }
    setActiveAccordionKey(CHARTING_INFO_ACCORDION_KEY)
    const frame = window.requestAnimationFrame(() => {
      document
        .querySelector('.perio-side-panel .charting-info-accordion-item')
        ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    })
    return (): void => {
      window.cancelAnimationFrame(frame)
    }
  }, [chartingInfoFocusTick])

  const toothIndex = getToothIndex(selectedTooth)

  const getFieldValue = (label: keyof PerioChartSectionData): string => {
    const raw = activePerioChart.perio_data[selectedSection][label][toothIndex]
    if (label === 'Mobility') return raw?.split(' ')[0] ?? '0'
    return raw ?? '0 0 0'
  }

  const validateSingleDigit = (inputValue: string): string => {
    // Remove all non-digit characters
    const digitsOnly = inputValue.replace(/\D/g, '')

    // If no digits found or empty, return '0'
    if (!digitsOnly || digitsOnly === '') {
      return '0'
    }

    // Return only the first digit (in case multiple digits were pasted)
    return digitsOnly.charAt(0)
  }

  const renderTripleInput = (
    label: keyof PerioChartSectionData,
  ): React.ReactElement[] => {
    const storageParts = getFieldValue(label).split(' ')
    if (label === 'Mobility') {
      return [
        <Form.Control
          key="mobility"
          className="text-center perio-triplet-input"
          value={storageParts[0]}
          onChange={(e) => {
            const validatedValue = validateSingleDigit(e.target.value)
            updatePerioValue(selectedSection, label, toothIndex, validatedValue)
          }}
          onFocus={(e) => {
            // Select all text when input is focused/clicked
            e.target.select()
          }}
          onClick={(e) => {
            // Select all text when input is clicked
            e.currentTarget.select()
          }}
          onKeyDown={(e) => {
            // If a digit is pressed, select all text first so it gets replaced
            if (/^[0-9]$/.test(e.key)) {
              const target = e.target as HTMLInputElement
              target.select()
              return
            }
            // Allow navigation keys, backspace, delete
            if (
              e.key === 'ArrowLeft' ||
              e.key === 'ArrowRight' ||
              e.key === 'Backspace' ||
              e.key === 'Delete' ||
              e.key === 'Tab' ||
              e.key === 'Enter'
            ) {
              return
            }
            // Prevent all other keys
            e.preventDefault()
          }}
          readOnly={isPerioLocked || isViewMode}
        />,
      ]
    }
    const displayParts = toDisplayOrder(selectedTooth, storageParts)
    return displayParts.map((val, i) => (
      <Form.Control
        key={i}
        className="text-center perio-triplet-input"
        value={val}
        onChange={(e) => {
          const validatedValue = validateSingleDigit(e.target.value)
          const nextDisplay = [...displayParts]
          nextDisplay[i] = validatedValue
          updatePerioValue(
            selectedSection,
            label,
            toothIndex,
            toStorageOrder(selectedTooth, nextDisplay).join(' '),
          )
        }}
        onFocus={(e) => {
          // Select all text when input is focused/clicked
          e.target.select()
        }}
        onClick={(e) => {
          // Select all text when input is clicked
          e.currentTarget.select()
        }}
        onKeyDown={(e) => {
          // If a digit is pressed, select all text first so it gets replaced
          if (/^[0-9]$/.test(e.key)) {
            const target = e.target as HTMLInputElement
            target.select()
            return
          }
          // Allow navigation keys, backspace, delete
          if (
            e.key === 'ArrowLeft' ||
            e.key === 'ArrowRight' ||
            e.key === 'Backspace' ||
            e.key === 'Delete' ||
            e.key === 'Tab' ||
            e.key === 'Enter'
          ) {
            return
          }
          // Prevent all other keys
          e.preventDefault()
        }}
        readOnly={isPerioLocked || isViewMode}
      />
    ))
  }

  const handleToggleDBMSurface = (
    label: 'Bleeding' | 'Calculus' | 'Supp' | 'Furcation',
    surface: 'D' | 'B' | 'M',
  ): void => {
    const current = getFieldValue(label).split(' ')
    const surfaceSet = new Set(current)
    if (surfaceSet.has(surface)) {
      surfaceSet.delete(surface)
    } else {
      surfaceSet.add(surface)
    }
    const updated = ['D', 'B', 'M']
      .map((s) => (surfaceSet.has(s) ? s : '0'))
      .join(' ')
    updatePerioValue(selectedSection, label, toothIndex, updated)
  }

  const renderDBMButtons = (
    label: 'Bleeding' | 'Calculus' | 'Supp' | 'Furcation',
  ): React.ReactElement[] => {
    const currentValueSet = new Set(getFieldValue(label).split(' '))

    return getProbeSiteLabels(selectedTooth).map((surface) => {
      const isSelected = currentValueSet.has(surface)
      return (
        <Button
          key={surface}
          size="sm"
          variant={isSelected ? 'primary' : 'secondary'}
          aria-pressed={isSelected}
          className="perio-dbm-buttons"
          onClick={(): void => handleToggleDBMSurface(label, surface)}
          disabled={isPerioLocked || isViewMode}
        >
          {surface}
        </Button>
      )
    })
  }

  const getSingleValue = (label: 'Mobility'): string => {
    const raw =
      activePerioChart?.perio_data?.[selectedSection]?.[label]?.[toothIndex]
    return raw?.split(' ')[0] || '0'
  }

  const updateSingleValue = (label: 'Mobility', value: string): void => {
    const sanitized = value.split(' ')[0]
    updatePerioValue(selectedSection, label, toothIndex, sanitized)
  }

  const isNewPerioChart =
    !createPerioChart?.chart_id?.trim() && !isPersistedChart

  const hasChartingValidationErrors = (): boolean => {
    return !!(
      (validationErrors.patient_id &&
        !selectedPatient &&
        !selectedPerioChart) ||
      (isChartProviderHardError(validationErrors.provider_id) &&
        isNewPerioChart) ||
      (validationErrors.title && isNewPerioChart)
    )
  }
  const patientError =
    !!validationErrors.patient_id && !selectedPatient && !selectedPerioChart
  const providerError =
    isChartProviderHardError(validationErrors.provider_id) && isNewPerioChart

  return (
    <div className="perio-side-panel">
      <Accordion
        className="charting-outer-accordion"
        openIds={openIdsFromKey(activeAccordionKey)}
        onOpenChange={(ids): void => {
          setActiveAccordionKey(ids[0] ?? '')
        }}
      >
        <AccordionItem
          id={CHARTING_INFO_ACCORDION_KEY}
          title="Charting information"
          className={classNames(
            'charting-outer-accordion-item charting-info-accordion-item',
            {
              'charting-info-accordion-item--invalid':
                hasChartingValidationErrors(),
            },
          )}
        >
          <div className="charting-info-fields">
            <InputField
              label="Patient name"
              variant="text"
              placeholder="Patient name"
              value={
                getFullName(selectedPerioChart?.patient ?? selectedPatient) ??
                'No Selected Patient'
              }
              readOnly
              fullWidth
              clearable={false}
              error={patientError}
              helperText={
                patientError ? validationErrors.patient_id : undefined
              }
            />
            {!isPersistedChart && !isViewMode ? (
              <Combobox
                label="Provider name"
                placeholder="Provider name"
                options={providerOptions}
                value={providerComboboxValue}
                allowCustom
                filterable
                disabled={isPerioLocked}
                error={providerError}
                helperText={
                  providerError ? validationErrors.provider_id : undefined
                }
                onChange={handleProviderChange}
              />
            ) : (
              <InputField
                label="Provider name"
                variant="text"
                placeholder="Provider name"
                value={resolveChartProviderDisplayName(
                  activePerioChart?.provider,
                  activePerioChart?.provider_id,
                  providerSearchList,
                )}
                readOnly
                fullWidth
                clearable={false}
              />
            )}
            {!isPersistedChart && !isViewMode ? (
              <ChartNameComboBox
                value={
                  createPerioChart ? createPerioChart.title : (chartName ?? '')
                }
                readOnly={isPerioLocked}
                disabled={isPerioLocked}
                isInvalid={!!validationErrors.title && isNewPerioChart}
                helperText={
                  validationErrors.title && isNewPerioChart
                    ? validationErrors.title
                    : undefined
                }
                onChange={(nextTitle): void => {
                  setChartName(nextTitle)
                  dispatch(
                    setCreatePerioChart({
                      ...createPerioChart,
                      title: nextTitle,
                    }),
                  )
                  if (nextTitle.trim()) {
                    setValidationErrors((prevErrors) => {
                      const updatedErrors = { ...prevErrors }
                      delete updatedErrors.title
                      return updatedErrors
                    })
                  }
                  dispatch(setPerioUpdated(true))
                }}
              />
            ) : (
              <InputField
                label="Chart name"
                variant="text"
                placeholder="Chart name"
                value={activePerioChart.title ?? ''}
                readOnly
                fullWidth
                clearable={false}
              />
            )}
          </div>
        </AccordionItem>

        <AccordionItem
          id="1"
          title="Treatment shortcuts"
          className="charting-outer-accordion-item"
        >
          <PerioFormRow label="Area" controlClassName="perio-area-select-wrap">
            <Select
              label=""
              className="perio-area-select"
              options={PERIO_AREA_OPTIONS}
              value={selectedSection}
              disabled={isPerioLocked}
              onChange={(nextValue): void => {
                if (isPerioSection(nextValue)) {
                  setSelectedSection(nextValue)
                }
              }}
            />
          </PerioFormRow>

          <PerioFormRow label="Tooth no.">
            <div className="counter-wrapper d-flex align-items-center justify-content-between w-100 input-container">
              <Button
                size="sm"
                variant="secondary"
                className="perio-counter-button"
                aria-label="Previous tooth"
                onClick={(): void =>
                  setSelectedTooth(Math.max(1, selectedTooth - 1))
                }
                disabled={isPerioLocked}
              >
                -
              </Button>
              <input
                value={selectedTooth}
                onChange={(e) => {
                  const val = Number(e.target.value)
                  if (val >= 1 && val <= 32) {
                    setSelectedTooth(val)
                  }
                }}
                min={1}
                max={32}
                className={`form-control text-center border-0 ${isPerioLocked ? 'disabled pe-none' : ''}`}
                style={{ width: '2.5rem', height: '1.75rem', padding: 0 }}
                readOnly={isPerioLocked}
              />
              <Button
                size="sm"
                variant="secondary"
                className="perio-counter-button"
                aria-label="Next tooth"
                onClick={(): void =>
                  setSelectedTooth(Math.min(32, selectedTooth + 1))
                }
                disabled={isPerioLocked}
              >
                +
              </Button>
            </div>
          </PerioFormRow>

          {voiceMeasurement && (
            <PerioFormRow label="Voice" htmlFor="perio-voice-measurement">
              {voiceMeasurement}
            </PerioFormRow>
          )}

          <PerioFormRow controlClassName="dbm-labels">
            <div className="input-container d-flex justify-content-center align-items-center gap-2 w-100">
              {getProbeSiteLabels(selectedTooth).map((site) => (
                <span key={site}>{site}</span>
              ))}
            </div>
          </PerioFormRow>

          <hr className="mb-2 mt-1" />

          {(['PD', 'GM', 'CAL'] as const).map((label) => (
            <PerioFormRow
              key={label}
              label={label}
              tight
              controlClassName="d-flex justify-content-center align-items-center gap-1"
            >
              {renderTripleInput(label)}
            </PerioFormRow>
          ))}

          {(['Bleeding', 'Calculus', 'Supp', 'Furcation'] as const).map(
            (label) => (
              <PerioFormRow
                key={label}
                label={label}
                tight
                className="dbm-buttons-container"
                controlClassName="d-flex justify-content-center align-items-center gap-1"
              >
                {renderDBMButtons(label)}
              </PerioFormRow>
            ),
          )}

          <PerioFormRow
            label="Mobility"
            tight
            controlClassName="d-flex justify-content-between align-items-center"
          >
            <div className="counter-wrapper perio-mobility-container">
              <Button
                size="sm"
                variant="secondary"
                className="perio-counter-button"
                aria-label="Decrease mobility"
                onClick={(): void => {
                  const current = parseInt(getSingleValue('Mobility'), 10) || 0
                  updateSingleValue(
                    'Mobility',
                    String(Math.max(0, current - 1)),
                  )
                }}
                disabled={isPerioLocked || isViewMode}
              >
                -
              </Button>
              <input
                min={0}
                max={9}
                value={getSingleValue('Mobility')}
                onChange={(e) => {
                  const val = Number(e.target.value)
                  if (val >= 0 && val <= 9) {
                    updateSingleValue('Mobility', String(val))
                  }
                }}
                className="form-control text-center border-0"
                style={{ width: '2.5rem', height: '1.75rem', padding: 0 }}
                readOnly={isPerioLocked || isViewMode}
              />
              <Button
                size="sm"
                variant="secondary"
                className="perio-counter-button"
                aria-label="Increase mobility"
                onClick={(): void => {
                  const current = parseInt(getSingleValue('Mobility'), 10) || 0
                  updateSingleValue(
                    'Mobility',
                    String(Math.min(9, current + 1)),
                  )
                }}
                disabled={isPerioLocked || isViewMode}
              >
                +
              </Button>
            </div>
          </PerioFormRow>
        </AccordionItem>
        {voiceStatus ? (
          <AccordionItem
            id={VOICE_TRANSCRIPTION_ACCORDION_KEY}
            title="Voice transcription"
            className="charting-outer-accordion-item"
          >
            {voiceStatus}
          </AccordionItem>
        ) : null}
      </Accordion>
    </div>
  )
}

export default PerioSidePanel
